// #4625 — did anything go wrong in this session?
//
// One in-memory flag, read by the review-prompt gate. If the app hit any
// problem since it started, we do not ask that person to rate it for the rest
// of the session — somebody whose app just misbehaved is the last person who
// should be handed a star rating, whether or not they noticed, and asking
// anyway is how a near-zero average gets worse rather than better.
//
// **Why this is a plain module and not a field on LoggingService.** That
// service is injectable with several dependencies and a separate
// initialisation step, and the test that pins the telemetry tiers next door is
// a source-TEXT test: it reads the file and asserts on strings, so it cannot
// prove behaviour. A plain module can be driven directly, which is the whole
// reason `problem-kind.ts` beside it exists.
//
// Deliberately NOT persisted. A fresh start is a clean slate. The point is not
// to punish an account for ever, it is not to ask somebody in a bad moment.
//
// See design-docs/review-prompts.md, "Nothing goes wrong before we ask".

/**
 * Set once anything has gone wrong. Module-level rather than per-instance
 * because there is one app session, and a page that reports a problem must
 * silence a prompt that a completely different page would fire.
 */
let somethingWentWrong = false;

/**
 * Record that something went wrong in this session.
 *
 * Called from `LoggingService`'s real-problem methods, so every error
 * path in the app is covered without touching a single call site. It is
 * deliberately NOT called by `trackSilentInfo`: that tier means a repair
 * already ran and nothing is wrong, and it fires routinely on healthy devices,
 * so including it would suppress the prompt almost always and the feature would
 * quietly never fire. Getting that backwards in either direction is the whole
 * subtlety — see design-docs/telemetry.md, "Three telemetry severity tiers".
 */
export function noteSomethingWentWrong(): void {
  somethingWentWrong = true;
}

/**
 * Does a problem-tier report say anything about the USER'S session?
 *
 * **The problem tier is not uniformly a problem for the person using the app.** Some
 * reports are correctly problem tier — the condition is unresolved and somebody may
 * need to act on it — and still say nothing about whether this person's session went
 * wrong: a slow cold start they walked away from, a login waiting because the device
 * is offline, a permanent property of workouts logged years ago, an operator's typo in
 * the config database. Counted, each would stop the app asking that person to rate it,
 * often on every session for ever.
 *
 * The call site that raises such a report says so, by passing `NotTheUsersSession` to
 * `LoggingService.trackSilentProblem`, and carries its own reason beside it. That keeps
 * the decision next to the name it applies to, so renaming a report cannot quietly
 * change whether it counts — which is what a list of names kept here used to allow.
 */
export enum SessionHealth {
  /** The default: this report means something went wrong in the session. */
  Counts = 'counts',
  /** This report is real, but it is not about the person's own session. */
  NotTheUsersSession = 'not-the-users-session',
}

/**
 * Does this problem-tier report name mean something actually went wrong?
 *
 * Only for reports whose NAME is built at runtime, so no call site can mark them with
 * `SessionHealth.NotTheUsersSession` once for every name they produce. Both are an
 * ordinary loss of connectivity: this app is offline-first, so a brief network failure
 * during initial sync is a normal Tuesday, and `trackAbnormalException` already refuses
 * to count exactly this class — counting it here would be the same over-suppression
 * arrived at from the other side.
 *
 * Keep this narrow. A fixed report name that is benign for the user is marked at its
 * call site instead; see `SessionHealth`.
 */
export function isRealProblemForSessionHealth(problemName: string): boolean {
  // the convention several deferred-read reports use to say so (the Home page's deferred
  // load). The singleton boot read used it too until #6248 replaced that report with
  // 'Db: boot held, account documents could not be read', which deliberately does count.
  if (problemName.includes('(transient network)')) return false;

  // a fetch that never reached the server. The name carries the URL, which is
  // why this is a prefix test and not an equality one.
  if (problemName.startsWith('Network error status 0 on URL')) return false;

  return true;
}

/**
 * Record a problem-tier report, unless its call site said it is not about the user's
 * session, or it is one of the network kinds above.
 */
export function noteSilentProblem(problemName: string, sessionHealth: SessionHealth = SessionHealth.Counts): void {
  // if the call site said this report is not about the user's session, it does not count
  if (sessionHealth === SessionHealth.NotTheUsersSession) return;
  if (!isRealProblemForSessionHealth(problemName)) return;
  somethingWentWrong = true;
}

/** Has anything gone wrong since this session started? */
export function didSomethingGoWrongThisSession(): boolean {
  return somethingWentWrong;
}

/**
 * Forget everything, for tests only.
 *
 * Production never clears this: the flag is per session by construction,
 * because a fresh start is a fresh module.
 */
export function resetSessionHealthForTests(): void {
  somethingWentWrong = false;
}
